import type { PerfectDrawProject } from "../projects/types";
import type { PersistedDocumentState } from "../comic/comicTypes";
import { projectDocumentState } from "../projects/projectMigration";
import { migratePersistedDocumentState } from "../projects/projectMigration";
import { decodeArtworkBlob } from "./artworkCodec";
import { getArtworkRecord, type StoredArtworkRecord } from "./artworkRepository";
import type { StoredRasterLayerBlob } from "../layers/layerTypes";
import { isAggregateRasterMemoryWithinBudget } from "../layers/layerMemory";

export type ArtworkRecoveryResult =
  | {
      kind: "blank";
      documentState: PersistedDocumentState;
      record?: undefined;
      blob?: undefined;
      rasterLayers: StoredRasterLayerBlob[];
    }
  | {
      kind: "current";
      record: StoredArtworkRecord;
      blob?: Blob;
      rasterLayers: StoredRasterLayerBlob[];
      documentState: PersistedDocumentState;
    }
  | {
      kind: "recovered";
      record: StoredArtworkRecord;
      blob?: Blob;
      rasterLayers: StoredRasterLayerBlob[];
      documentState: PersistedDocumentState;
    }
  | { kind: "corrupt"; record: StoredArtworkRecord; blob?: undefined; documentState?: undefined };

async function canDecode(blob: Blob, project: PerfectDrawProject): Promise<boolean> {
  try {
    const decoded = await decodeArtworkBlob(blob, { width: project.width, height: project.height });
    decoded.release();
    return true;
  } catch {
    return false;
  }
}

async function canDecodeLayers(
  layers: ReadonlyArray<StoredRasterLayerBlob>,
  project: PerfectDrawProject,
  state: PersistedDocumentState,
): Promise<boolean> {
  const expected = state.layers.filter((layer) => layer.type === "raster").map((layer) => layer.id);
  if (
    layers.length !== expected.length ||
    new Set(layers.map((layer) => layer.layerId)).size !== layers.length
  )
    return false;
  if (!isAggregateRasterMemoryWithinBudget(project.width, project.height, layers)) return false;
  for (const id of expected) {
    const layer = layers.find((candidate) => candidate.layerId === id);
    if (!layer || layer.mimeType !== "image/png") return false;
    if (!layer.blank && (!layer.blob || !(await canDecode(layer.blob, project)))) return false;
  }
  return true;
}

function revisionLayers(
  record: StoredArtworkRecord,
  state: PersistedDocumentState,
  revision: "current" | "previous",
): StoredRasterLayerBlob[] | undefined {
  const layers = revision === "current" ? record.currentLayers : record.previousLayers;
  if (layers) return layers;
  if (revision === "current" && record.schemaVersion >= 3) return undefined;
  const blob = revision === "current" ? record.currentBlob : record.previousBlob;
  if (!blob) return undefined;
  const first = state.layers.find((layer) => layer.type === "raster");
  if (!first) return undefined;
  return state.layers
    .filter((layer) => layer.type === "raster")
    .map((layer) => ({
      layerId: layer.id,
      mimeType: "image/png" as const,
      blank: layer.id !== first.id,
      blob: layer.id === first.id ? blob : undefined,
    }));
}

function readRevisionState(
  value: PersistedDocumentState | undefined,
  record: StoredArtworkRecord,
  project: PerfectDrawProject,
): PersistedDocumentState | undefined {
  try {
    if (value) return migratePersistedDocumentState(value, project);
    // Milestone 3 records predate paired document state. Their project metadata
    // is the only structural source and is safe after project migration.
    if (record.schemaVersion < 2) return projectDocumentState(project);
  } catch {
    return undefined;
  }
  return undefined;
}

export async function recoverArtwork(project: PerfectDrawProject): Promise<ArtworkRecoveryResult> {
  const fallbackState = projectDocumentState(project);
  const record = await getArtworkRecord(project.id);
  if (!record) return { kind: "blank", documentState: fallbackState, rasterLayers: [] };

  const currentState = readRevisionState(record.currentDocumentState, record, project);
  const currentLayers = currentState ? revisionLayers(record, currentState, "current") : undefined;
  if (
    currentState &&
    currentLayers &&
    (await canDecodeLayers(currentLayers, project, currentState))
  ) {
    return {
      kind: "current",
      record,
      blob: record.currentBlob ?? currentLayers.find((layer) => !layer.blank)?.blob,
      rasterLayers: currentLayers,
      documentState: currentState,
    };
  }

  const previousState = readRevisionState(record.previousDocumentState, record, project);
  const previousLayers = previousState
    ? revisionLayers(record, previousState, "previous")
    : undefined;
  if (
    previousState &&
    previousLayers &&
    (await canDecodeLayers(previousLayers, project, previousState))
  ) {
    return {
      kind: "recovered",
      record,
      blob: record.previousBlob ?? previousLayers.find((layer) => !layer.blank)?.blob,
      rasterLayers: previousLayers,
      documentState: previousState,
    };
  }
  return { kind: "corrupt", record };
}
